/**
 * Audience filter selections → population filter: the one builder (C09-1).
 *
 * A filter is picked as selections: values of single columns, and, for a
 * question split over several columns (a group), the options picked and the
 * values each picked option must hold. Options of one group combine with the
 * group's logic (OR by default: "any of these"); columns and groups combine
 * with AND. The audience filter modal's manual pick, its ask-AI chips and
 * Nest's ask-AI count preview all build their filter here, so the AI's
 * "people who own a dog" is the same filter, and the same count, as ticking
 * the dog options of the pets question by hand.
 */

export interface SelectionFilterCondition {
  field: string;
  operator: "equals" | "in";
  value: string | Array<string>;
  /** Present when the condition targets a whole binary group. */
  groupType?: "binary" | "multiselect";
  /** The group's member columns (binary group form only). */
  members?: Array<string>;
}

export interface SelectionFilter {
  logic: "AND" | "OR";
  conditions: Array<SelectionFilterCondition | SelectionFilter>;
}

/** A single filterable column: its name and raw answer values. */
export interface FilterSelectionColumn {
  name: string;
  uniqueValues: Array<string>;
  valueLabels?: Record<string, string>;
}

/** One option (member column) of a group, with its own answer values. */
export interface FilterGroupOption {
  column: string;
  uniqueValues: Array<string>;
  valueLabels?: Record<string, string>;
}

/** A question group: one question whose options are separate columns. */
export interface FilterGroupEntry {
  groupId: string;
  groupType: "binary" | "multiselect";
  memberColumns: Array<string>;
  /** Union of the members' raw values, one per distinct label, by label. */
  uniqueValues: Array<string>;
  valueLabels?: Record<string, string>;
  options?: Array<FilterGroupOption>;
}

/** The two-row pick of a group: the options, and the values they must hold. */
export interface FilterGroupRowSelection {
  options: Set<string>;
  values: Set<string>;
}

/**
 * Segmentation categories whose columns are always single filters, never
 * members of a group (demographics, firmographics and the like).
 */
export const SINGLE_ONLY_FILTER_CATEGORIES: ReadonlySet<string> = new Set([
  "geography",
  "demographics",
  "firmographics",
  "professional",
]);

function distinctByLabel(
  values: Array<string>,
  labels: Record<string, string>,
): Array<string> {
  const hasLabels = Object.keys(labels).length > 0;
  const seen = new Set<string>();
  const distinct = values.filter((value) => {
    const label = labels[value] ?? value;
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });
  return distinct.sort((a, b) =>
    ((hasLabels ? labels[a] : undefined) ?? a).localeCompare(
      (hasLabels ? labels[b] : undefined) ?? b,
    ),
  );
}

/**
 * A group's values and options from its member columns. Null when no member
 * has a value.
 */
export function filterGroupEntry(
  group: {
    groupId: string;
    groupType: "binary" | "multiselect";
    memberColumns: Array<string>;
  },
  columns: Array<FilterSelectionColumn>,
): FilterGroupEntry | null {
  const memberSet = new Set(group.memberColumns);
  const members = columns.filter((column) => memberSet.has(column.name));
  if (members.length === 0) return null;

  const valueLabels: Record<string, string> = {};
  for (const column of members) {
    if (column.valueLabels) Object.assign(valueLabels, column.valueLabels);
  }
  const uniqueValues = distinctByLabel(
    [...new Set(members.flatMap((column) => column.uniqueValues))],
    valueLabels,
  );
  if (uniqueValues.length === 0) return null;

  const options = members
    .map((column) => {
      const labels = column.valueLabels ?? {};
      return {
        column: column.name,
        uniqueValues: distinctByLabel(column.uniqueValues, labels),
        valueLabels: Object.keys(labels).length > 0 ? labels : undefined,
      };
    })
    .filter((option) => option.uniqueValues.length > 0);

  return {
    groupId: group.groupId,
    groupType: group.groupType,
    memberColumns: group.memberColumns,
    uniqueValues,
    valueLabels: Object.keys(valueLabels).length > 0 ? valueLabels : undefined,
    options: options.length > 0 ? options : undefined,
  };
}

const SELECTED_NOT_SELECTED = new Set(["selected", "not selected"]);

/**
 * True when a group's values are exactly "Selected" / "Not selected" (the
 * typical shape of checkbox groups). Only these groups hide the Values row.
 */
export function isSelectedNotSelectedGroup(
  uniqueValues: Array<string>,
  valueLabels?: Record<string, string> | null,
): boolean {
  if (uniqueValues.length !== 2) return false;
  return uniqueValues
    .map((value) => (valueLabels?.[value] ?? value).toLowerCase().trim())
    .every((label) => SELECTED_NOT_SELECTED.has(label));
}

export function resolvePositiveValue(
  uniqueValues: Array<string>,
  valueLabels?: Record<string, string> | null,
): string {
  return (
    uniqueValues.find(
      (value) =>
        (valueLabels?.[value] ?? value).toLowerCase().trim() === "selected",
    ) ?? uniqueValues[0]
  );
}

/**
 * Expand a two-row group pick into the per-member value map
 * (`memberColumn → Set<value>`) {@link buildFilterFromSelections} reads: every
 * picked option gets the picked values. A "Selected / Not selected" group with
 * options and no values means "Selected".
 */
export function materializeGroupMembers(
  group: Pick<FilterGroupEntry, "options" | "uniqueValues" | "valueLabels">,
  rowSel: FilterGroupRowSelection,
): Record<string, Set<string>> {
  if (rowSel.options.size === 0) return {};

  let effectiveValues = rowSel.values;
  if (
    effectiveValues.size === 0 &&
    isSelectedNotSelectedGroup(group.uniqueValues, group.valueLabels)
  ) {
    effectiveValues = new Set([
      resolvePositiveValue(group.uniqueValues, group.valueLabels),
    ]);
  }
  if (effectiveValues.size === 0) return {};

  const members: Record<string, Set<string>> = {};
  for (const option of group.options ?? []) {
    if (rowSel.options.has(option.column)) {
      members[option.column] = new Set(effectiveValues);
    }
  }
  return members;
}

/**
 * Which options of a multiselect group are a real constraint (and so appear in
 * the filter, and on its chip):
 * - an option with no values never is;
 * - when a strict subset of options is active, every active option is, even
 *   with all its values (the omitted options are the constraint);
 * - when all options are active, only those not holding all their values are.
 */
export function multiselectGroupConstrainedOptions<
  Option extends FilterGroupOption,
>(
  group: { options?: Array<Option> },
  memberSel: Record<string, Set<string>>,
): Array<Option> {
  const options = group.options ?? [];
  const activeCount = options.reduce((n, option) => {
    const selected = memberSel[option.column];
    return n + (selected && selected.size > 0 ? 1 : 0);
  }, 0);
  const optionsAreScoped = activeCount > 0 && activeCount < options.length;
  return options.filter((option) => {
    const selected = memberSel[option.column];
    if (!selected || selected.size === 0) return false;
    if (optionsAreScoped) return true;
    return !(
      option.uniqueValues.length > 0 &&
      option.uniqueValues.every((value) => selected.has(value))
    );
  });
}

function valueCondition(
  field: string,
  values: Array<string>,
): SelectionFilterCondition {
  return {
    field,
    operator: values.length === 1 ? "equals" : "in",
    value: values.length === 1 ? values[0] : values,
  };
}

/**
 * The population filter of a pick, or null when the pick constrains nothing.
 *
 * Single columns (opt-in: nothing selected is no filter; every value selected
 * is no filter, except on a column a saved segment injected): `column IN
 * values`. A selection on a column outside `columns` (a resolved identity
 * column the ask-AI catalog offers, or a segment's column) is a constraint
 * whenever it holds a value. Multiselect groups: each constrained option is
 * `option IN values`, and a group's options combine with its logic (OR by
 * default). Binary groups: the picked values over all members. Everything is
 * ANDed at the top.
 */
export function buildFilterFromSelections(
  selections: Record<string, Set<string>>,
  columns: Array<FilterSelectionColumn>,
  groupSelections?: Record<string, Set<string>>,
  groupEntries?: Array<FilterGroupEntry>,
  groupMemberSelections?: Record<string, Record<string, Set<string>>>,
  groupLogic?: Record<string, "OR" | "AND">,
  /** Columns injected from saved segments: all values selected still filters. */
  syntheticColumns?: Set<string>,
): SelectionFilter | null {
  const conditions: Array<SelectionFilterCondition | SelectionFilter> = [];

  const known = new Set<string>();
  for (const column of columns) {
    known.add(column.name);
    const selected = selections[column.name];
    if (!selected || selected.size === 0) continue;
    if (
      !syntheticColumns?.has(column.name) &&
      selected.size === column.uniqueValues.length
    ) {
      continue;
    }
    conditions.push(valueCondition(column.name, Array.from(selected)));
  }
  for (const [name, selected] of Object.entries(selections)) {
    if (known.has(name) || !selected || selected.size === 0) continue;
    conditions.push(valueCondition(name, Array.from(selected)));
  }

  for (const group of groupEntries ?? []) {
    if (group.options && groupMemberSelections) {
      const memberSel = groupMemberSelections[group.groupId];
      if (!memberSel) continue;
      const optionConditions = multiselectGroupConstrainedOptions(
        group,
        memberSel,
      ).map((option) =>
        valueCondition(option.column, Array.from(memberSel[option.column])),
      );
      if (optionConditions.length === 0) continue;
      conditions.push(
        optionConditions.length === 1
          ? optionConditions[0]
          : {
              logic: groupLogic?.[group.groupId] ?? "OR",
              conditions: optionConditions,
            },
      );
      continue;
    }
    if (groupSelections) {
      const selected = groupSelections[group.groupId];
      if (!selected || selected.size === 0) continue;
      if (selected.size === group.uniqueValues.length) continue;
      conditions.push({
        ...valueCondition(group.groupId, Array.from(selected)),
        groupType: group.groupType,
        members: group.memberColumns,
      });
    }
  }

  return conditions.length > 0 ? { logic: "AND", conditions } : null;
}

/**
 * What the ask-AI filter picks, in the picker's own vocabulary: values of one
 * column, or a group's options and the values they must hold. Several options
 * of one group mean "any of them", as a manual pick does.
 */
export interface AskAiFilterChoice {
  column?: string;
  groupId?: string;
  /** The group's member columns picked (group choices only). */
  options?: Array<string>;
  values: Array<string>;
}

/**
 * One ask-AI choice as Nest returns it and the picker shows it (a chip). A
 * group choice's `column` is its `groupId`: the chip's key.
 */
export interface AskAiFilterCondition extends AskAiFilterChoice {
  column: string;
  displayQuestion: string;
  displayAnswer: string;
  chip: string;
}

export interface FilterSelectionsFromAi {
  selections: Record<string, Set<string>>;
  groupRowSelections: Record<string, FilterGroupRowSelection>;
  groupMemberSelections: Record<string, Record<string, Set<string>>>;
}

/**
 * The picker's selections for ask-AI choices: a column choice selects its
 * values; a group choice picks its options and values, exactly as the two-row
 * group pick does. A group the entries do not know is no selection.
 */
export function selectionsFromAiChoices(
  choices: Array<AskAiFilterChoice>,
  groupEntries: Array<FilterGroupEntry>,
): FilterSelectionsFromAi {
  const byId = new Map(groupEntries.map((group) => [group.groupId, group]));
  const result: FilterSelectionsFromAi = {
    selections: {},
    groupRowSelections: {},
    groupMemberSelections: {},
  };
  for (const choice of choices) {
    if (choice.groupId) {
      const group = byId.get(choice.groupId);
      if (!group) continue;
      const row: FilterGroupRowSelection = {
        options: new Set(choice.options ?? []),
        values: new Set(choice.values),
      };
      result.groupRowSelections[group.groupId] = row;
      result.groupMemberSelections[group.groupId] = materializeGroupMembers(
        group,
        row,
      );
    } else if (choice.column) {
      result.selections[choice.column] = new Set(choice.values);
    }
  }
  return result;
}
